import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import App from "./App";
import { ConfettiProvider } from "./context/ConfettiContext";
import GlobalConfetti from "./context/GlobalConfetti";
import { initGA } from "./services/GoogleAnalytics";
import { normalizeLangParam, resolveInterfaceLang } from "./lib/interfaceLang";
import { CONTENT_LANG_KEY, detectBrowserInterfaceLanguage, INTERFACE_LANG_KEY, setInterfaceLanguage } from "./services/LangPrefs";
import { ensureLocaleReady } from "./pages/locales";
import { appendScreenshotLang, applyScreenshotLangFromQuery } from "./lib/screenshotI18n";
import { applyScreenshotDocumentLang } from "./lib/screenshotFonts";
import "./styles/appWebViewLegal.css";
import "./styles/immersivePlay.css";
import "./index.css";
import reportWebVitals from "./reportWebVitals";

// Amplify
// import { Amplify } from "aws-amplify";
// import awsExports from "./aws-exports";
// Amplify.configure(awsExports);

// Initialize Google Analytics (only in production)
initGA();

// Chromium reports this when a ResizeObserver callback schedules layout in the same frame.
// It is usually harmless; we defer our observers with rAF, but MUI/other libs may still trigger it.
window.addEventListener(
    "error",
    (event) => {
        if (typeof event?.message === "string" && event.message.includes("ResizeObserver loop")) {
            event.stopImmediatePropagation();
        }
    },
    true
);

const root = ReactDOM.createRoot(document.getElementById("root"));

/** Web-only: `?snapshot_ui_lang=french&snapshot_content_lang=english` seeds LangPrefs before first render. */
function applySnapshotQueryParamsToLangPrefs() {
    try {
        const q = new URLSearchParams(window.location.search);
        const ui = q.get("snapshot_ui_lang");
        const content = q.get("snapshot_content_lang");
        if (ui) {
            const resolved = resolveInterfaceLang(ui);
            localStorage.setItem(INTERFACE_LANG_KEY, resolved);
            applyScreenshotDocumentLang(resolved);
        }
        if (content) {
            const resolved = normalizeLangParam(content) ?? content;
            localStorage.setItem(CONTENT_LANG_KEY, resolved);
        }
    } catch {
        /* ignore */
    }
}

/** iOS UI tests: native bridge may set path and/or lang before document parse. */
function applySnapshotConfigFromNative() {
    try {
        const nativeLang = window.__PUZZLE_SNAPSHOT_LANG__;
        if (nativeLang) {
            const resolved = resolveInterfaceLang(nativeLang);
            localStorage.setItem(INTERFACE_LANG_KEY, resolved);
            applyScreenshotDocumentLang(resolved);
        }

        const p = window.__PUZZLE_SNAPSHOT_PATH__;
        if (typeof p !== "string" || !p.startsWith("/")) return;

        let target = p;
        const uiLang = resolveInterfaceLang(localStorage.getItem(INTERFACE_LANG_KEY));
        if (uiLang && uiLang !== "english" && !/[?&]lang=/i.test(p)) {
            target = appendScreenshotLang(p, uiLang);
        }
        window.history.replaceState(null, "", target);
    } catch {
        /* ignore */
    }
}

async function initApp() {
    applySnapshotQueryParamsToLangPrefs();
    applyScreenshotLangFromQuery();
    applySnapshotConfigFromNative();

    /** First visit: pick UI language from the browser if we support it, otherwise English. Puzzle content stays English. */
    try {
        const storedUi = localStorage.getItem(INTERFACE_LANG_KEY);
        if (!storedUi) {
            setInterfaceLanguage(detectBrowserInterfaceLanguage());
        } else {
            const resolvedUi = resolveInterfaceLang(storedUi);
            if (resolvedUi !== storedUi) {
                setInterfaceLanguage(resolvedUi);
            }
        }
        if (localStorage.getItem(CONTENT_LANG_KEY) === "french") {
            localStorage.setItem(CONTENT_LANG_KEY, "english");
        }
    } catch {
        /* ignore */
    }

    await ensureLocaleReady();
    root.render(
        <React.StrictMode>
            <BrowserRouter
                future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true,
                }}
            >
                <HelmetProvider>
                    <ConfettiProvider>
                        <App />
                        <GlobalConfetti />
                    </ConfettiProvider>
                </HelmetProvider>
            </BrowserRouter>
        </React.StrictMode>
    );
}

// Wait for the document (and WebView) to finish loading before first API-heavy render.
// If load already fired, run immediately (e.g. some navigations / HMR).
function scheduleInitApp() {
    const run = () => {
        initApp().catch((err) => {
            console.error("Failed to start app:", err);
        });
    };
    if (document.readyState === "complete") {
        run();
    } else {
        window.addEventListener("load", () => {
            run();
        });
    }
}

scheduleInitApp();

// Production web only: register SW so Chrome can offer PWA install (address-bar install / “download app”).
// Invalid manifest icons or missing SW are common reasons the install control never appears.
if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
    window.addEventListener("load", () => {
        const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
        navigator.serviceWorker.register(`${base}/sw.js`).catch(() => {
            /* ignore */
        });
    });
} else if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
        navigator.serviceWorker.getRegistrations()
            .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
            .catch(() => {
                /* ignore */
            });
    });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
